import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import { Loading } from '@components/atoms/Loading';
import Alert from '@components/atoms/Alert';
import PaymentMethodSelector from '@pages/register/components/PaymentMethodSelector';
import {
  BillingPeriodSelector,
  PlanSelectionCards,
  reactivateNewCardSchema,
} from '@components/organisms/subscription';
import { useSubscriptionPlans } from '@hooks/api/subscription/useSubscriptionPlans';
import { useActivateSubscriptionAfterTrial } from '@hooks/api/subscription/useActivateSubscriptionAfterTrial';
import { useTrialStatus } from '@hooks/useTrialStatus';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { useTranslation } from '@hooks/useTranslation';

/**
 * The paywall — the one way out of TRIAL_EXPIRED.
 *
 * Reuses the plan cards and card form from the reactivation flow rather than growing a
 * second payment UI. What differs is the framing: nothing failed here and nothing was
 * lost, so this reads as "pick a plan", not "your account is in trouble".
 *
 * Only an OWNER can pay. Everyone else gets an explanation rather than a button that would
 * be refused server-side — the same split the suspended-organization page uses.
 */
const TrialExpiredPage: React.FC = () => {
  // Sprint 28, story-143. Bucket 2 — see docs/architecture.md "## Sprint 28" Decision 4.
  const tOrgLifecycle = useTranslation('orgLifecycle');
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);

  const { organizationId, name: organizationName } = useAppSelector(
    (state: RootState) => state.organization,
  );
  const { role, firstName, lastName, email } = useAppSelector(
    (state: RootState) => state.user,
  );
  const { isTrialExpired, isLoading: isLoadingTrial } = useTrialStatus();

  const isOwner = role === UserRoles.OWNER;

  const { data: subscriptionPlans, isLoading: isLoadingPlans } =
    useSubscriptionPlans({ enabled: isOwner });
  const { mutateAsync, isPending } = useActivateSubscriptionAfterTrial();

  const publicPlans = useMemo(
    () => (subscriptionPlans || []).filter((plan) => plan.isPublic && !plan.isAddOn),
    [subscriptionPlans],
  );

  const formik = useFormik({
    initialValues: {
      paymentMethodType: 'credit_card',
      billingPeriod: 'MONTHLY',
      selectedPlanId: '',
      billingFirstName: firstName || '',
      billingLastName: lastName || '',
      billingEmail: email || '',
      billingAddress1: '',
      billingAddress2: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      billingCountry: 'US',
      billingPhone: '',
      ccNumber: '',
      ccExp: '',
      cvv: '',
    },
    validationSchema: reactivateNewCardSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!organizationId) return;
      setError(null);

      try {
        await mutateAsync({
          organizationId,
          paymentMethodType: values.paymentMethodType.toUpperCase(),
          billingCycle: values.billingPeriod.toUpperCase(),
          planId: values.selectedPlanId || undefined,
          firstName: values.billingFirstName,
          lastName: values.billingLastName,
          email: values.billingEmail,
          address1: values.billingAddress1,
          address2: values.billingAddress2 || undefined,
          city: values.billingCity,
          state: values.billingState,
          zip: values.billingZip,
          country: values.billingCountry || 'US',
          phone: values.billingPhone || undefined,
          // NMI wants digits only.
          ccNumber: values.ccNumber.replace(/\s/g, ''),
          ccExp: values.ccExp.replace(/\//g, ''),
          cvv: values.cvv,
        });

        history.push('/dashboard');
      } catch (err: any) {
        setError(
          err?.response?.errors?.[0]?.message ||
            err?.message ||
            tOrgLifecycle.activateSubscriptionError,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
  });

  if (isLoadingTrial) {
    return <Loading size="2xl" center />;
  }

  // Reachable by anyone who bookmarks the URL or lands here after paying in another tab.
  if (!isTrialExpired) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {tOrgLifecycle.nothingToDoHere}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {tOrgLifecycle.subscriptionGoodStanding}
          </p>
          <Button className="mt-6" onClick={() => history.push('/dashboard')}>
            {tOrgLifecycle.backToDashboard}
          </Button>
        </Card>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {tOrgLifecycle.trialEndedForOrg.replace('{org}', organizationName || '')}
          </h1>
          <p className="mt-3 text-gray-600 dark:text-gray-400">
            {tOrgLifecycle.teamCanStillOpen}
          </p>
          <p className="mt-3 text-gray-600 dark:text-gray-400">
            {tOrgLifecycle.onlyOwnerCanRestore}
          </p>
          <Button
            variant="secondary"
            className="mt-6"
            onClick={() => history.push('/dashboard')}>
            {tOrgLifecycle.backToDashboard}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {tOrgLifecycle.choosePlanForOrg.replace('{org}', organizationName || '')}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {tOrgLifecycle.trialEndedAddPayment}
        </p>
      </div>

      {error && (
        <Alert variant="error" showIcon title={tOrgLifecycle.paymentCouldNotBeProcessed}>
          <p>{error}</p>
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
        {isLoadingPlans ? (
          <Loading size="lg" center />
        ) : (
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {tOrgLifecycle.planLabel}
            </h2>
            <BillingPeriodSelector
              selectedPeriod={formik.values.billingPeriod}
              onPeriodChange={(period) =>
                formik.setFieldValue('billingPeriod', period)
              }
            />
            <PlanSelectionCards
              plans={publicPlans}
              selectedPlanId={formik.values.selectedPlanId}
              billingPeriod={formik.values.billingPeriod}
              onSelectPlan={(planId: string) =>
                formik.setFieldValue('selectedPlanId', planId)
              }
            />
          </Card>
        )}

        <Card>
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {tOrgLifecycle.paymentDetails}
          </h2>
          <PaymentMethodSelector
            formik={formik}
            userData={{
              firstName: firstName || '',
              lastName: lastName || '',
              email: email || '',
            }}
            showBillingCycle={false}
          />
        </Card>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => history.push('/dashboard')}>
            {tOrgLifecycle.back}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? <Loading size="sm" /> : tOrgLifecycle.activateSubscription}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TrialExpiredPage;
